import { useCallback, useEffect, useState } from "react";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import {
	useGetListLeadSyncTriggerQuery,
	useGetOAuthStatusQuery,
	useUpdateListLeadSyncTriggerMutation,
} from "@/pages/Private/redux/hubspot/hubspot.api";

import { FeaturesCodes } from "../../../../Users/enum/codes";
import { HubspotLeadSyncTriggerWithInherit } from "../../../../Company/schema/hubspot";

interface UseHubspotLeadSyncTriggerOptions {
	listId: number;
}

export const useHubspotLeadSyncTrigger = ({ listId }: UseHubspotLeadSyncTriggerOptions) => {
	const isHubspotEnabled = useIsFeatureActive(FeaturesCodes.HUBSPOT);
	const { user } = useAppSelector(authSelector);
	const hasHubspotApiKey = user?.company?.hasHubspotApiKey ?? false;
	const { data: oauthStatus } = useGetOAuthStatusQuery(undefined, { skip: !isHubspotEnabled });
	const isConnected = hasHubspotApiKey || (oauthStatus?.connected ?? false);
	const { data: listLeadSyncTrigger } = useGetListLeadSyncTriggerQuery(listId, {
		skip: !isHubspotEnabled || !isConnected || !listId,
	});
	const [updateListLeadSyncTrigger] = useUpdateListLeadSyncTriggerMutation();
	const [selectedLeadSyncTrigger, setSelectedLeadSyncTrigger] = useState<
		HubspotLeadSyncTriggerWithInherit | undefined
	>(undefined);

	useEffect(() => {
		if (listLeadSyncTrigger) {
			// Existing list-setting hooks keep API-backed dropdown state locally for save-on-submit.
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setSelectedLeadSyncTrigger(listLeadSyncTrigger.trigger);
		}
	}, [listLeadSyncTrigger]);

	const hasLeadSyncTriggerChanged = useCallback(
		() =>
			isHubspotEnabled &&
			isConnected &&
			selectedLeadSyncTrigger !== undefined &&
			selectedLeadSyncTrigger !== listLeadSyncTrigger?.trigger,
		[isHubspotEnabled, isConnected, selectedLeadSyncTrigger, listLeadSyncTrigger?.trigger]
	);

	const saveLeadSyncTrigger = useCallback(async () => {
		if (hasLeadSyncTriggerChanged() && selectedLeadSyncTrigger) {
			await updateListLeadSyncTrigger({
				listId,
				data: { trigger: selectedLeadSyncTrigger },
			}).unwrap();
		}
	}, [hasLeadSyncTriggerChanged, selectedLeadSyncTrigger, listId, updateListLeadSyncTrigger]);

	return {
		listLeadSyncTrigger,
		selectedLeadSyncTrigger,
		setSelectedLeadSyncTrigger,
		saveLeadSyncTrigger,
	};
};
